import type { EChartsOption } from 'echarts';

/**
 * 透传的流式 answer item.value 数据形态：echarts option 本体 + 容器尺寸/配色覆写
 */
export type TAgentEchartValuePayload = Partial<EChartsOption> & {
  height?: number | string;
  width?: number | string;
  color?: string[];
};

/**
 * AgentEchart组件属性接口
 */
export interface IAgentEchartProps {
  /**
   * 透传的原始数据（流式 answer item.value），形态为 { ...echartsOption, height?, width?, color? }
   * 当显式传入 options/height/width/color 时，以显式 props 优先；否则从 value 中派生。
   */
  value?: TAgentEchartValuePayload;
  /** echarts配置选项 */
  options?: EChartsOption;
  /** 图表颜色列表，默认为项目默认配色 */
  color?: string[];
  /** 图表高度，默认300px */
  height?: number | string;
  /** 图表宽度，默认100% */
  width?: number | string;
  /** 自定义样式类名 */
  className?: string;
  /** 自定义样式 */
  style?: React.CSSProperties;
  /** 消息ID，用于标识不同的消息会话 */
  messageId?: string;
}

/**
 * 项目默认的echart配色
 */
export const DEFAULT_COLOR_LIST = [
  '#5086F3', // 蓝色
  '#77E097', // 绿色
  '#6A7597', // 灰色
  '#F8CC6A', // 黄色
  '#A08FF2', // 紫色
  '#5EC8F1', // 青色
  '#CF61AC', // 粉色
  '#F19E55', // 橙色
  '#387F7D', // 深绿色
  '#F17C86', // 红色
  '#8EB9FF', // 浅蓝色
] as const;

/**
 * 图表事件类型
 */
export type TChartEventType = 
  | 'click'
  | 'dblclick'
  | 'mousedown'
  | 'mousemove'
  | 'mouseup'
  | 'mouseover'
  | 'mouseout'
  | 'globalout'
  | 'contextmenu'
  | 'legendselectchanged'
  | 'legendselected'
  | 'legendunselected'
  | 'datazoom'
  | 'datarangeselected'
  | 'timelinechanged'
  | 'timelineplaychanged'
  | 'restore'
  | 'dataviewchanged'
  | 'magictypechanged'
  | 'geoselectchanged'
  | 'geoselected'
  | 'geounselected'
  | 'pieselectchanged'
  | 'pieselected'
  | 'pieunselected'
  | 'mapselectchanged'
  | 'mapselected'
  | 'mapunselected'
  | 'axisareaselected'
  | 'focusnodeadjacency'
  | 'unfocusnodeadjacency'
  | 'brush'
  | 'brushselected'
  | 'globalcursortaken'
  | 'rendered'
  | 'finished';

/**
 * 图表事件参数类型
 */
export interface IChartEventParams {
  /** 事件类型 */
  type: TChartEventType;
  /** 事件名称 */
  name: string;
  /** 事件数据 */
  data?: any;
  /** 事件值 */
  value?: any;
  /** 系列索引 */
  seriesIndex?: number;
  /** 数据索引 */
  dataIndex?: number;
  /** 系列名称 */
  seriesName?: string;
  /** 系列类型 */
  seriesType?: string;
  /** 组件名称 */
  componentType?: string;
  /** 组件索引 */
  componentIndex?: number;
  /** 事件对象 */
  event?: Event;
  /** 图表实例 */
  chart?: any;
}
